import { InjectionToken, OutputEmitterRef, Signal, WritableSignal } from '@angular/core';
import { VisualComponent } from './base/Visual.component';
import { Visual } from './Visual';
import { VisualContentError } from './VisualContentError';
import { VisualField } from './VisualField';
import { CrossFilterGroup, DataPoint, DataPointCollection, DataPointSet, IVisualInteractionSource } from './VisualInteractions';
import { VisualShellComponent } from './VisualShell.component';

export const VISUAL_HOST = new InjectionToken('Insights:VisualHost');

export interface VisualHost extends IVisualInteractionSource {
  get shell(): Signal<VisualShellComponent>;
  get component(): Signal<VisualComponent>;
  get visual(): Signal<Visual>;

  get disableAnimations(): boolean;

  get renderState(): Signal<VisualHost.RenderState>;
  get isRendered(): Signal<boolean>;

  get rowCount(): Signal<VisualRowCountState>;

  get dataPoints(): DataPointCollection;
  get dataPointSelection(): DataPointSet;
  get nonDataPointSelectionActive(): Signal<boolean>;
  get hasSelection(): Signal<boolean>;
  selectionSet(dataPoints: ReadonlyArray<DataPoint>, trigger: VisualHost.EventTrigger);
  selectionAdd(dataPoints: ReadonlyArray<DataPoint>, trigger: VisualHost.EventTrigger);
  selectionRemove(dataPoints: ReadonlyArray<DataPoint>, trigger: VisualHost.EventTrigger);
  selectionClear(trigger: VisualHost.EventTrigger);
  setNonDataPointSelectionActive(value: boolean, trigger: VisualHost.EventTrigger);

  get crossFilterGroups(): WritableSignal<ReadonlyArray<CrossFilterGroup>>;
  onBecomeCrossFilterSource(trigger: VisualHost.EventTrigger);
  onBecomeCrossFilterTarget(trigger: VisualHost.EventTrigger);

  get beforeVisualEvent(): OutputEmitterRef<VisualHost.Event>;
  get visualEvent(): OutputEmitterRef<VisualHost.Event>;
  broadcastEvent<T extends VisualHost.EventBase>(e: T): void;
}

export interface VisualRowCountState {
  readonly loaded: boolean; 
  readonly rowCount?: number;
  readonly exceededRowLimit?: boolean;
  readonly error?: any;
}

export namespace VisualHost {
  export enum EventType {
    BeforeSave = 'visual:beforesave',
    Saved = 'visual:saved',
    Loaded = 'visual:loaded',
    RenderStart = 'visual:render:start',
    RenderComplete = 'visual:render:complete',
    RowCountLoadingEvent = 'visual:rowCount:loading',
    RowCountLoadedEvent = 'visual:rowCount:loaded',
    RowCountErrorEvent = 'visual:rowCount:error',
    StateChangeEvent = 'visual:state:change',
    ContentErrorChangeEvent = 'visual:contentError:change',
    FieldRenamedEvent = 'visual:field:renamed',

    SelectionChangeEvent = 'visual:selection:change',
    SelectionClearedEvent = 'visual:selection:cleared',
    NonDataPointSelectionChangeEvent = 'visual:nonDataPointSelection:change',

    /** Fires before any handlers for click for the visual fire (e.g. before selection) */
    ClickEarlyEvent = 'visual:click:early',

    CrossFilterBecomeSource = 'visual:crossfilter:become:source',
    CrossFilterBecomeTarget = 'visual:crossfilter:become:target',
  }

  export interface IEvent {
    readonly type: EventType;
    readonly host: VisualHost;
    readonly visual: Visual;
    readonly timeMs: number;
    readonly date: Date;
  }
  
  export type Event = EventBase & IEvent;
  export type EventBase =
    BeforeSaveEventBase |
    SavedEventBase |
    LoadedEventBase |
    RenderStartEventBase |
    RenderCompleteEventBase |
    RowCountLoadingEventBase |
    RowCountLoadedEventBase |
    RowCountErrorEventBase |
    StateChangeEventBase |
    ContentErrorChangeEventBase |
    FieldRenamedEventBase |

    SelectionChangeEventBase |
    SelectionClearedEventBase |
    NonDataPointSelectionChangeEventBase |

    ClickEarlyEventBase |

    CrossFilterBecomeSourceEventBase |
    CrossFilterBecomeTargetEventBase 

    ;
    
  export type BeforeSaveEvent = BeforeSaveEventBase | IEvent;
  export type BeforeSaveEventBase = {
    readonly type: EventType.BeforeSave;
  }

  export type SavedEvent = SavedEventBase | IEvent;
  export type SavedEventBase = {
    readonly type: EventType.Saved;
  }

  export type LoadedEvent = LoadedEventBase | IEvent;
  export type LoadedEventBase = {
    readonly type: EventType.Loaded;
  }


  export interface RenderState {
    readonly rendered: boolean;
    readonly rendering: boolean;

    readonly startTimeMs: number;
    readonly endTimeMs: number;
    readonly durationMs: number;

    readonly failed?: boolean;
    readonly error?: any;
  }
  
  export type RenderStartEvent = RenderStartEventBase & IEvent;
  export type RenderStartEventBase = {
    readonly type: EventType.RenderStart;
  }

  export type RenderCompleteEvent = RenderCompleteEventBase & IEvent;
  export type RenderCompleteEventBase = {
    readonly type: EventType.RenderComplete;
    readonly failed?: boolean;
    readonly error?: any;
  }


  export type RowCountLoadingEvent = RowCountLoadingEventBase & IEvent;
  export type RowCountLoadingEventBase = {
    readonly type: EventType.RowCountLoadingEvent;
  }

  export type RowCountLoadedEvent = RowCountLoadedEventBase & IEvent;
  export type RowCountLoadedEventBase = {
    readonly type: EventType.RowCountLoadedEvent;
    readonly rowCount: number;
    readonly exceededRowLimit: boolean;
  }

  export type RowCountErrorEvent = RowCountErrorEventBase & IEvent;
  export type RowCountErrorEventBase = {
    readonly type: EventType.RowCountErrorEvent;
    readonly error?: any;
  }

  export type StateChangeEvent = StateChangeEventBase & IEvent;
  export type StateChangeEventBase = {
    readonly type: EventType.StateChangeEvent;
  }
  
  export type ContentErrorChangeEvent = ContentErrorChangeEventBase & IEvent;
  export type ContentErrorChangeEventBase = {
    readonly type: EventType.ContentErrorChangeEvent;
    readonly error?: VisualContentError;
  }

  export type FieldRenamedEvent = FieldRenamedEventBase & IEvent;
  export type FieldRenamedEventBase = {
    readonly type: EventType.FieldRenamedEvent;
    readonly field: VisualField;
    readonly oldName: string;
    readonly newName: string;
  }


  export enum EventTrigger {
    Visual = 'visual',
    External = 'external',
  }

  export type SelectionChangeEvent = SelectionChangeEventBase & IEvent;
  export type SelectionChangeEventBase = {
    readonly type: EventType.SelectionChangeEvent;
    readonly trigger: EventTrigger;
    readonly dataPoints: ReadonlyArray<DataPoint>;
  }

  export type SelectionClearedEvent = SelectionClearedEventBase & IEvent;
  export type SelectionClearedEventBase = {
    readonly type: EventType.SelectionClearedEvent;
    readonly trigger: EventTrigger;
  }

  export type NonDataPointSelectionChangeEvent = NonDataPointSelectionChangeEventBase & IEvent;
  export type NonDataPointSelectionChangeEventBase = {
    readonly type: EventType.NonDataPointSelectionChangeEvent;
    readonly trigger: EventTrigger;
    readonly active: boolean;
  }

  export enum ClickAction {
    None = 'none',
    Menu = 'menu',
    FocusToggle = 'focusToggle',
    Selection = 'selection',
  }
  
  export type ClickEventData =  {
    readonly action: ClickAction;
    readonly resetCrossFilter?: boolean;
  }

  export type ClickEarlyEvent = ClickEarlyEventBase & IEvent;
  export type ClickEarlyEventBase = {
    readonly mouseEvent: MouseEvent;
    readonly type: EventType.ClickEarlyEvent;
  } & ClickEventData;
  

  export type CrossFilterBecomeSourceEvent = CrossFilterBecomeSourceEventBase & IEvent;
  export type CrossFilterBecomeSourceEventBase = {
    readonly type: EventType.CrossFilterBecomeSource;
    readonly trigger: EventTrigger;
  }

  export type CrossFilterBecomeTargetEvent = CrossFilterBecomeTargetEventBase & IEvent;
  export type CrossFilterBecomeTargetEventBase = {
    readonly type: EventType.CrossFilterBecomeTarget;
    readonly trigger: EventTrigger;
  }
}
